import { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { useMutation } from '@apollo/client/react';
import {
  FileRefFragment,
  DocumentFragment,
  AddFileToContractDocument,
  ConfirmFileUploadedToContractDocument,
  DeleteFileFromContractDocument,
  UpdateFileInContractDocument,
  AddDocumentToContractDocument,
  DeleteDocumentFromContractDocument,
  UpdateDocumentInContractDocument,
  AddFileToContractMutation,
  AddFileToContractMutationVariables,
  ConfirmFileUploadedToContractMutation,
  ConfirmFileUploadedToContractMutationVariables,
  DeleteFileFromContractMutation,
  DeleteFileFromContractMutationVariables,
  UpdateFileInContractMutation,
  UpdateFileInContractMutationVariables,
  AddDocumentToContractMutation,
  AddDocumentToContractMutationVariables,
  DeleteDocumentFromContractMutation,
  DeleteDocumentFromContractMutationVariables,
  UpdateDocumentInContractMutation,
  UpdateDocumentInContractMutationVariables,
  GetAccountByIdDocument,
  GetAccountByIdQuery,
  GetAccountByIdQueryVariables,
} from '../../../generated/graphql';
import { useAlert } from '@dwelvio/react-alert';
import { Box, Button, Stack, Tab, Tabs, Typography } from '@mui/material';
import { AddIcon } from '../../../components/icons';
import { DocumentCreateOrUploadDialog } from '../../../components/document-create-or-upload-dialog';
import { DocumentItemDetailDialog, DocumentItemData } from '../../../components/document-item-detail-dialog';
import { UnifiedDocumentList, UnifiedItem } from '../../../components/unified-document-list';
import { uploadFile } from '../../../lib/upload-files';
import { fileContentDisposition } from '@dwelvio/shared';
import { useNavigate } from 'react-router';
import { appPaths } from '../../../app-paths';
import { useAuth } from '@dwelvio/auth-react';
import { IUserData } from '../../../types';

interface ContractDocumentsSectionProps {
  contractId: string;
  files: FileRefFragment[];
  documents: DocumentFragment[];
  disabled?: boolean;
}

export function ContractDocumentsSection({ contractId, files, documents, disabled }: ContractDocumentsSectionProps) {
  const { show } = useAlert();
  const navigate = useNavigate();
  const { user } = useAuth<IUserData>();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [detailItem, setDetailItem] = useState<DocumentItemData | null>(null);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [savingItem, setSavingItem] = useState(false);
  const [deletingItem, setDeletingItem] = useState(false);
  const [selectedTab, setSelectedTab] = useState('all');

  const { data: accountData } = useQuery<GetAccountByIdQuery, GetAccountByIdQueryVariables>(GetAccountByIdDocument, {
    variables: { id: user?.accountId },
    skip: !user?.accountId,
  });

  const [addFile] = useMutation<AddFileToContractMutation, AddFileToContractMutationVariables>(AddFileToContractDocument);
  const [confirmUpload] = useMutation<ConfirmFileUploadedToContractMutation, ConfirmFileUploadedToContractMutationVariables>(
    ConfirmFileUploadedToContractDocument,
  );
  const [deleteFile] = useMutation<DeleteFileFromContractMutation, DeleteFileFromContractMutationVariables>(
    DeleteFileFromContractDocument,
    { onError: (e) => show.error('Nepodarilo sa zmazať súbor.', e.message) },
  );
  const [updateFile] = useMutation<UpdateFileInContractMutation, UpdateFileInContractMutationVariables>(
    UpdateFileInContractDocument,
    { onError: (e) => show.error('Nepodarilo sa uložiť súbor.', e.message) },
  );

  const [addDocument] = useMutation<AddDocumentToContractMutation, AddDocumentToContractMutationVariables>(
    AddDocumentToContractDocument,
    { onError: (e) => show.error('Nepodarilo sa vytvoriť dokument.', e.message) },
  );
  const [deleteDocument] = useMutation<DeleteDocumentFromContractMutation, DeleteDocumentFromContractMutationVariables>(
    DeleteDocumentFromContractDocument,
    { onError: (e) => show.error('Nepodarilo sa zmazať dokument.', e.message) },
  );
  const [updateDocument] = useMutation<UpdateDocumentInContractMutation, UpdateDocumentInContractMutationVariables>(
    UpdateDocumentInContractDocument,
    { onError: (e) => show.error('Nepodarilo sa uložiť dokument.', e.message) },
  );

  const handleUpload = useCallback(
    async (file: File, _title: string, _description: string) => {
      const headers = { 'Content-Disposition': fileContentDisposition(file.name) };
      setUploadInProgress(true);
      try {
        await uploadFile(
          file,
          (f) => addFile({ variables: { contractId, type: 'cd', input: { filename: f.name, mimeType: f.type, size: f.size } } }),
          (data) => data.addFileToContract.uploadUrl,
          (f, data) => confirmUpload({ variables: { contractId, fileId: data.addFileToContract.fileId, size: f.size } }),
          headers,
        );
      } catch (error) {
        show.error('Chyba pri nahrávaní súboru', (error as Error).message);
      } finally {
        setUploadInProgress(false);
      }
    },
    [addFile, confirmUpload, show, contractId],
  );

  const handleCreate = useCallback(
    async (title: string, description: string) => {
      await addDocument({ variables: { contractId, input: { title, description } } });
    },
    [addDocument, contractId],
  );

  const handleItemClick = useCallback(
    (item: UnifiedItem, isTouchDevice: boolean) => {
      if (isTouchDevice) {
        if (item.type === 'file') {
          setDetailItem({
            id: item.data.id,
            title: item.data.name ?? item.data.id,
            description: item.data.desc,
            tags: item.data.tags,
            downloadUrl: item.data.url,
            fileName: item.data.name,
            fileSize: item.data.size,
          });
        } else {
          setDetailItem({
            id: item.data.id,
            title: item.data.title,
            description: item.data.description,
            tags: item.data.tags,
            documentContent: item.data.content,
            downloadUrl: null,
          });
        }
      } else {
        if (item.type === 'file') {
          const a = document.createElement('a');
          a.href = item.data.url;
          a.download = item.data.name ?? '';
          a.target = '_blank';
          a.click();
        } else {
          navigate(appPaths.document.navigate({ documentId: item.data.id }));
        }
      }
    },
    [navigate],
  );

  const handleEditClick = useCallback((item: UnifiedItem) => {
    if (item.type === 'file') {
      setDetailItem({
        id: item.data.id,
        title: item.data.name ?? item.data.id,
        description: item.data.desc,
        tags: item.data.tags,
        downloadUrl: item.data.url,
        fileName: item.data.name,
        fileSize: item.data.size,
      });
    } else {
      setDetailItem({
        id: item.data.id,
        title: item.data.title,
        description: item.data.description,
        tags: item.data.tags,
        documentContent: item.data.content,
        downloadUrl: null,
      });
    }
  }, []);

  const handleDetailSave = useCallback(
    async (title: string, description: string, tags: string[]) => {
      if (!detailItem) return;
      setSavingItem(true);
      try {
        const isFile = files.some((f) => f.id === detailItem.id);
        if (isFile) {
          await updateFile({
            variables: { contractId, fileId: detailItem.id, input: { name: title, desc: description, tags } },
          });
        } else {
          await updateDocument({
            variables: { contractId, documentId: detailItem.id, input: { title, description, tags } },
          });
        }
      } finally {
        setSavingItem(false);
      }
    },
    [detailItem, files, updateFile, updateDocument, contractId],
  );

  const handleDetailDelete = useCallback(async () => {
    if (!detailItem) return;
    setDeletingItem(true);
    try {
      const isFile = files.some((f) => f.id === detailItem.id);
      if (isFile) {
        await deleteFile({ variables: { contractId, fileId: detailItem.id } });
      } else {
        await deleteDocument({ variables: { contractId, documentId: detailItem.id } });
      }
    } finally {
      setDeletingItem(false);
    }
  }, [detailItem, files, deleteFile, deleteDocument, contractId]);

  const accountDocumentTags = useMemo(
    () => (accountData?.getAccountById?.tags ?? []).filter((tag) => tag.entityType === 'document'),
    [accountData],
  );

  const allTags = useMemo(() => {
    const tagsByName = new Map<string, { name: string; color?: string | null }>();
    for (const tag of accountDocumentTags) {
      tagsByName.set(tag.name.toLocaleLowerCase(), { name: tag.name, color: tag.color });
    }
    for (const document of documents) {
      for (const tagName of document.tags ?? []) {
        const key = tagName.toLocaleLowerCase();
        if (!tagsByName.has(key)) {
          tagsByName.set(key, { name: tagName, color: null });
        }
      }
    }
    for (const file of files) {
      for (const tagName of file.tags ?? []) {
        const key = tagName.toLocaleLowerCase();
        if (!tagsByName.has(key)) {
          tagsByName.set(key, { name: tagName, color: null });
        }
      }
    }
    return Array.from(tagsByName.values()).sort((a, b) => a.name.localeCompare(b.name, 'sk'));
  }, [accountDocumentTags, documents, files]);

  const allItems = useMemo(
    () =>
      [
        ...files.filter((f) => !f.deletedAt).map((f) => ({ type: 'file' as const, data: f })),
        ...documents.filter((d) => !d.deletedAt).map((d) => ({ type: 'document' as const, data: d })),
      ].sort((a, b) => {
        const titleA = a.type === 'file' ? (a.data.name ?? '') : a.data.title;
        const titleB = b.type === 'file' ? (b.data.name ?? '') : b.data.title;
        return titleA.localeCompare(titleB, 'sk');
      }),
    [documents, files],
  );

  const tagTabs = useMemo(
    () => allTags.map((tag) => ({ key: tag.name.toLocaleLowerCase(), name: tag.name, color: tag.color })),
    [allTags],
  );

  useEffect(() => {
    if (selectedTab !== 'all' && !tagTabs.some((tag) => tag.key === selectedTab)) {
      setSelectedTab('all');
    }
  }, [selectedTab, tagTabs]);

  const tagColors = useMemo(
    () =>
      allTags.reduce(
        (acc, tag) => {
          acc[tag.name.toLocaleLowerCase()] = tag.color;
          return acc;
        },
        {} as Record<string, string | null | undefined>,
      ),
    [allTags],
  );

  const items: UnifiedItem[] = useMemo(() => {
    if (selectedTab === 'all') {
      return allItems;
    }
    return allItems.filter((item) => (item.data.tags ?? []).some((tag) => tag.toLocaleLowerCase() === selectedTab));
  }, [allItems, selectedTab]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h2">Dokumenty</Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          disabled={disabled}
          onClick={() => setShowCreateDialog(true)}
        >
          Pridať dokument
        </Button>
      </Stack>

      <Tabs value={selectedTab} onChange={(_event, value) => setSelectedTab(value)} sx={{ marginTop: 1 }}>
        <Tab value="all" label="All" />
        {tagTabs.map((tag) => (
          <Tab
            key={tag.key}
            value={tag.key}
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: tag.color ?? 'text.disabled',
                  }}
                />
                <Box component="span">{tag.name}</Box>
              </Stack>
            }
          />
        ))}
      </Tabs>

      {items.length === 0 && (
        <Typography variant="h5" color="text.secondary">
          Neboli nájdené žiadne dokumenty.
        </Typography>
      )}

      {items.length > 0 && (
        <UnifiedDocumentList
          items={items}
          onItemClick={handleItemClick}
          onEditClick={handleEditClick}
          disabled={disabled}
          tagColors={tagColors}
        />
      )}

      {showCreateDialog && (
        <DocumentCreateOrUploadDialog
          open={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          onCreate={handleCreate}
          onUpload={handleUpload}
          creating={false}
          uploading={uploadInProgress}
        />
      )}

      {detailItem && (
        <DocumentItemDetailDialog
          open={!!detailItem}
          item={detailItem}
          onClose={() => setDetailItem(null)}
          onSave={handleDetailSave}
          onDelete={handleDetailDelete}
          availableTags={allTags}
          saving={savingItem}
          deleting={deletingItem}
          disabled={disabled}
        />
      )}
    </Stack>
  );
}
